import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Scale, FileText, Calendar, Info, ExternalLink, Radar } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import ArticleCard from "@/components/ArticleCard";
import NewsletterBox from "@/components/NewsletterBox";
import { FALLBACK_IMAGE } from "@/data/constants";

const Hero = ({ stories }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (stories.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % stories.length), 6000);
    return () => clearInterval(t);
  }, [stories.length]);

  if (!stories.length) return null;
  const s = stories[idx];

  return (
    <section className="relative bg-[#0A192F] h-[520px] overflow-hidden" data-testid="hero-carousel">
      <img src={s.image_url || FALLBACK_IMAGE} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/80 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
        <div className="max-w-2xl fade-up" key={idx}>
          <span className="inline-block bg-[#D4AF37] text-[#0A192F] overline px-3 py-1 mb-4">Top Story</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
            {s.title}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed line-clamp-3 max-w-xl">
            {s.excerpt}
          </p>
          <Link
            to={`/news/${s.slug}`}
            data-testid="hero-read-story"
            className="inline-block mt-7 bg-[#D4AF37] text-[#0A192F] overline px-7 py-3 hover:bg-[#e6c14e] transition-colors"
          >
            Read Full Story
          </Link>
        </div>
      </div>
      {stories.length > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + stories.length) % stories.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-9 h-9" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % stories.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-9 h-9" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {stories.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#D4AF37]" : "w-2 bg-white/40"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const RadarBanner = ({ items }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;

  return (
    <section className="relative bg-[#0A192F] border-b-4 border-[#D4AF37]" data-testid="radar-banner">
      <div className="max-w-7xl mx-auto px-4 pt-5 flex items-center gap-3">
        <span className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] overline px-3 py-1">
          <Radar className="w-4 h-4" /> Wolfdog News Radar
        </span>
        <span className="inline-flex items-center gap-1.5 overline text-[#D4AF37]">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live · updates every 72h
        </span>
      </div>

      <div className="overflow-hidden mt-4">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {items.map((r) => (
            <a
              key={r.id}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`radar-banner-item-${r.id}`}
              className="group relative w-full shrink-0 h-[300px] sm:h-[380px] block"
            >
              <img
                src={r.image_url || FALLBACK_IMAGE}
                alt={r.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-[#0A192F]/20" />
              <div className="relative max-w-7xl mx-auto px-4 h-full flex flex-col justify-center">
                <span className="overline text-[#D4AF37]">{r.source}</span>
                <h2 className="font-display text-2xl sm:text-4xl font-black text-white leading-tight tracking-tight max-w-3xl mt-1 group-hover:text-[#D4AF37] transition-colors">
                  {r.title}
                </h2>
                <p className="mt-3 text-sm sm:text-base text-white/70 max-w-2xl line-clamp-2 leading-relaxed">
                  {r.summary}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 overline text-[#D4AF37]">
                  Read at source <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
            className="absolute left-3 top-[58%] -translate-y-1/2 text-white/60 hover:text-[#D4AF37] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % items.length)}
            className="absolute right-3 top-[58%] -translate-y-1/2 text-white/60 hover:text-[#D4AF37] transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/40"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const QuickLink = ({ Icon, title, desc, to, testid }) => (  <Link to={to} data-testid={testid} className="group flex items-start gap-4 p-2">
    <div className="w-12 h-12 rounded-full bg-[#0A192F] flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37] transition-colors">
      <Icon className="w-5 h-5 text-[#D4AF37] group-hover:text-[#0A192F] transition-colors" />
    </div>
    <div>
      <h4 className="overline text-[#0A192F]">{title}</h4>
      <p className="text-sm text-[#4A5568] leading-snug mt-1">{desc}</p>
      <span className="overline text-[#8B6D1F] mt-2 inline-block group-hover:underline">Learn More →</span>
    </div>
  </Link>
);

export default function Home() {
  useSeo({ description: "Wolfdog News is the trusted source for wolfdog news, state-by-state legislation, responsible ownership education, rescue resources, and community events." });
  const [top, setTop] = useState([]);
  const [latest, setLatest] = useState([]);
  const [radar, setRadar] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.get("/articles/top-story").then((r) => setTop(r.data)).catch(() => {});
    api.get("/articles", { params: { status: "published", limit: 7 } }).then((r) => setLatest(r.data)).catch(() => {});
    api.get("/radar", { params: { limit: 8 } }).then((r) => setRadar(r.data)).catch(() => {});
    api.get("/trending").then((r) => setTrending(r.data)).catch(() => {});
  }, []);

  const mainLatest = latest.slice(0, 4);

  return (
    <div>
      <RadarBanner items={radar} />

      {/* Latest news */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center gap-4 mb-8">
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A192F]">Latest News</h2>
          <div className="flex-1 h-px bg-[#0A192F]/15" />
          <Link to="/news" className="overline text-[#8B6D1F] hover:underline">View All</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {mainLatest.map((a) => (
            <ArticleCard key={a.id} article={a} compact />
          ))}
        </div>
        {!mainLatest.length && (
          <p className="text-[#4A5568]">No articles yet. Add stories from the CMS.</p>
        )}
      </section>

      <Hero stories={top} />

      {/* Quick links */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <QuickLink Icon={Scale} title="Legislation Center" to="/legislation" testid="quick-legislation" desc="State, and federal legislation that impacts wolfdog owners." />
          <QuickLink Icon={FileText} title="Articles & Guides" to="/articles" testid="quick-articles" desc="In-depth articles on wolfdog care, training, and behavior." />
          <QuickLink Icon={Calendar} title="Events Calendar" to="/events" testid="quick-events" desc="Find wolfdog events, rescues, and community meetups." />
          <QuickLink Icon={Info} title="Information Hub" to="/information" testid="quick-information" desc="Learn about wolfdogs, ownership, and responsible care." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16" id="newsletter">
        <NewsletterBox />
        <div className="bg-white border border-[#0A192F]/10 p-6 mt-8">
          <h3 className="overline text-[#0A192F] mb-4">Trending Topics</h3>
          <div className="flex flex-wrap gap-2">
            {trending.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="text-xs px-3 py-1.5 border border-[#0A192F]/20 text-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-colors rounded-full"
                data-testid={`trending-${t}`}
              >
                {t}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
